import axios from 'axios';
import store from './store';
import { useToast } from 'vue-toastification';

const toast = useToast();
let isRefreshing = false;
let refreshQueue = [];

function processQueue(error, token = null) {
  refreshQueue.forEach(p => {
    if (error) {
      p.reject(error);
    } else {
      p.resolve(token);
    }
  });
  refreshQueue = [];
}

axios.interceptors.request.use(config => {
  const token = store.getters.stateToken;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

axios.interceptors.response.use(r => r, async error => {
  const original = error.config;
  if (!error.response) {
    toast.error('Error de red. Verifica tu conexión.');
    return Promise.reject(error);
  }
  const { status, data } = error.response;
  const isRefreshCall = original?.url?.includes('/auth/refresh_token');

  // Treat explicit invalid/expired token detail similarly (backend may vary status code)
  const expiredDetail = data?.detail === 'Invalid token or expired token.';

  if ((status === 401 || expiredDetail) && !original._retry && !isRefreshCall) {
    original._retry = true;
    try {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          refreshQueue.push({ resolve, reject });
        }).then(token => {
          original.headers.Authorization = `Bearer ${token}`;
          return axios(original);
        });
      }
      isRefreshing = true;
      const refreshToken = store.state.users?.refreshToken || localStorage.getItem('refreshToken');
      if (!refreshToken) throw new Error('No refresh token');
      const newToken = await store.dispatch('refreshToken', refreshToken);
      processQueue(null, newToken);
      original.headers.Authorization = `Bearer ${newToken}`;
      return axios(original);
    } catch (e) {
      processQueue(e, null);
      toast.error('Sesión expirada. Inicia sesión nuevamente.');
      store.dispatch('logOut');
      return Promise.reject(e);
    } finally {
      isRefreshing = false;
    }
  }

  // Some backend responses may return 403 instead of 401 for expired/invalid tokens
  if (status === 403 && (data?.detail === 'Not authenticated' || expiredDetail) && !original._retry && !isRefreshCall) {
    original._retry = true;
    try {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          refreshQueue.push({ resolve, reject });
        }).then(token => {
          original.headers.Authorization = `Bearer ${token}`;
          return axios(original);
        });
      }
      isRefreshing = true;
      const refreshToken = store.state.users?.refreshToken || localStorage.getItem('refreshToken');
      if (!refreshToken) throw new Error('No refresh token');
      const newToken = await store.dispatch('refreshToken', refreshToken);
      processQueue(null, newToken);
      original.headers.Authorization = `Bearer ${newToken}`;
      return axios(original);
    } catch (e) {
      processQueue(e, null);
      toast.error('Sesión expirada. Inicia sesión nuevamente.');
      store.dispatch('logOut');
      return Promise.reject(e);
    } finally {
      isRefreshing = false;
    }
  }

  if (status === 403 && data?.code) {
    // Map known limit codes
    const messages = {
      proceedings_limit_reached: 'Límite de expedientes alcanzado. Mejora tu plan.',
      notifications_limit_reached: 'Límite de notificaciones alcanzado.',
      no_plan: 'No tienes un plan activo. Selecciona uno para continuar.',
      plan_limit_zero: 'Tu plan no permite esta acción.'
    };
    const msg = messages[data.code];
    if (msg) {
      toast.warning(msg);
      // Optionally trigger upsell modal via store.dispatch('ui/showUpsell', data.code)
    }
  }

  return Promise.reject(error);
});

export default axios;
